import { useLocale } from "@/hooks/useLocale";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils/cn";
import { SPRING } from "./sidebar.constants";
import type { SpaceKey } from "./sidebar.types";

const SPACES: SpaceKey[] = ["myspace", "company"];

interface SpaceSwitcherProps {
  activeSpace: SpaceKey;
  isCollapsed: boolean;
  onSwitch: (space: SpaceKey) => void;
}

/** Toggles between "My Space" and "Company" — collapsed pill dots or expanded chrome-tabs. */
export const SpaceSwitcher = ({
  activeSpace,
  isCollapsed,
  onSwitch,
}: SpaceSwitcherProps) => {
  const { t } = useTranslation("common");
  const { byDir } = useLocale();
  const labelOf = (space: SpaceKey) =>
    space === "myspace" ? t("spaces.my_space") : t("spaces.company");

  return (
    <div
      className={cn(
        "shrink-0",
        isCollapsed && "border-b border-border py-2 px-1.5",
      )}
    >
      {isCollapsed ? (
        <div className="flex flex-col gap-1.5 items-center">
          {SPACES.map((space) => (
            <Tooltip key={space}>
              <TooltipTrigger asChild>
                <motion.button
                  onClick={() => onSwitch(space)}
                  aria-label={labelOf(space)}
                  aria-pressed={activeSpace === space}
                  animate={{
                    width: activeSpace === space ? 32 : 20,
                    backgroundColor:
                      activeSpace === space
                        ? "var(--color-primary)"
                        : "var(--color-border)",
                  }}
                  transition={SPRING}
                  className="h-1.5 rounded-full"
                />
              </TooltipTrigger>
              <TooltipContent side={byDir("right", "left")}>
                {labelOf(space)}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      ) : (
        // Chrome-tab style — active tab is a white card flush against the
        // panel below (no seam, same fill as the content beneath it);
        // inactive tab blends into the muted strip. Base corners flare
        // outward via the classic two-layer trick: a small tab-colored
        // square sits right at the corner, and a bigger muted-colored
        // circle (2x the square, same outer offset) overlaps it — the
        // square keeps the edge flush with the tab, the circle carves the
        // curve into it, together reading as one continuous flare. The pieces
        // sit at a logical start/end but are pushed out with a transform,
        // which is physical — hence the rtl: flips.
        <div className="flex items-end w-full bg-muted px-3 pt-2">
          {SPACES.map((space) => {
            const isActiveSpace = activeSpace === space;

            return (
              <button
                key={space}
                onClick={() => onSwitch(space)}
                aria-pressed={isActiveSpace}
                className={cn(
                  // Only the text colour animates. The new active tab's corner flares
                  // (muted circles) overlap its neighbour at once, so a fading
                  // background on the tab just left showed them as a grey blink.
                  "relative flex-1 rounded-t-sm px-3 py-1.5 lg:py-2 text-xs lg:text-sm text-center transition-[color] duration-150",
                  isActiveSpace
                    ? "z-10 bg-white text-foreground font-semibold"
                    : "font-medium text-muted-foreground hover:z-20 hover:bg-white/50 hover:text-foreground",
                )}
              >
                {labelOf(space)}
                {isActiveSpace && (
                  <>
                    <span
                      aria-hidden
                      className="absolute bottom-0 start-0 z-10 h-1.5 w-1.5 -translate-x-full rtl:translate-x-full bg-white"
                    />
                    <span
                      aria-hidden
                      className="absolute bottom-0 start-0 z-20 h-3 w-3 -translate-x-full rtl:translate-x-full rounded-full bg-muted"
                    />
                    <span
                      aria-hidden
                      className="absolute bottom-0 end-0 z-10 h-1.5 w-1.5 translate-x-full rtl:-translate-x-full bg-white"
                    />
                    <span
                      aria-hidden
                      className="absolute bottom-0 end-0 z-20 h-3 w-3 translate-x-full rtl:-translate-x-full rounded-full bg-muted"
                    />
                  </>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
